import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { SCREEN_ID_HOUSEHOLD_BUYER_SIDE_AGENT } from 'client/constants/screenIds';

import { SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_BUYERS_AGENT } from 'api/actionTypes';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';

import branch from 'client/core/branch';
import HouseholdActions from 'client/actions/Household';
import HouseholdListingActions from 'client/actions/HouseholdListing';

import SrMultiStepForm, {
  SR_MULTI_STEP_FORM_THEME_HOUSEHOLD_BUYER_SIDE_AGENT_PAGE,
} from 'client/components/SrMultiStepForm';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrBuyerSideAgentFormContainer from 'client/components/SrBuyerSideAgentFormPage/SrBuyerSideAgentFormContainer';
import SrBuyerSideAgentFormControl from 'client/components/SrBuyerSideAgentFormPage/SrBuyerSideAgentFormControl';

const makeFormStepsGetterFunc = ({ buyerSideAgent }) => {
  return () => {
    const steps = {
      'buyer-agent': {
        fieldIds: ['hasBuyersAgent'],
      },
      'has-realtor': {
        fieldIds: ['hasRealtor'],
        ui: {
          noStaticFooter: true,
        },
      },
      'supra-agent': {
        fieldIds: ['supraAgent'],
        ui: {
          legend: ({ onForceNext }) => (
            <SrBox padding="32px 0 4px">
              <SrText
                font="greycliff"
                color="blue300"
                weight="800"
                lineHeight="19px"
                cursor="pointer"
                onClick={onForceNext}
              >
                I don't see my agent
              </SrText>
            </SrBox>
          ),
        },
      },
      'buyers-agent-name': {
        fieldIds: ['buyersAgentName'],
        ui: {
          noStaticFooter: true,
        },
      },
      'buyers-agent-phone': {
        fieldIds: ['buyersAgentPhone'],
        ui: {
          noStaticFooter: true,
        },
      },
      'buyers-agent-email': {
        fieldIds: ['buyersAgentEmail'],
        ui: {
          noStaticFooter: true,
        },
      },
    };

    const stepIds = buyerSideAgent
      ? ['buyer-agent']
      : [
          'has-realtor',
          'supra-agent',
          'buyers-agent-name',
          'buyers-agent-phone',
          'buyers-agent-email',
        ];

    return stepIds
      .map((stepId) => {
        return {
          id: stepId,
          ...steps[stepId],
        };
      })
      .map((step) => ({
        ...step,
        component: SrBuyerSideAgentFormContainer,
        ui: {
          legend: step.ui?.legend,
          noStaticFooter: step.ui?.noStaticFooter,
          controlComponent: SrBuyerSideAgentFormControl,
          theme: SR_MULTI_STEP_FORM_THEME_HOUSEHOLD_BUYER_SIDE_AGENT_PAGE,
        },
      }));
  };
};

function makeFormFieldsGetterFunc({ buyerSideAgent, supraAgents }) {
  return () => {
    return lodashCompact([
      {
        id: 'hasRealtor',
        title: 'Have you worked with a realtor to find this home?',
        validations: ['required'],
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          choices: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
        },
      },
      {
        id: 'buyersAgentName',
        title: 'Name of the person who showed you the property?',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['required'],
        type: 'short_text',
      },
      {
        id: 'buyersAgentPhone',
        title: 'What is their phone number?',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['phone', 'required'],
        type: 'phone',
      },
      {
        id: 'buyersAgentEmail',
        title: 'What is their email?',
        properties: {
          placeholder: 'Write here...',
        },
        validations: ['email', 'required'],
        type: 'email',
      },
      buyerSideAgent && {
        id: 'hasBuyersAgent',
        title: 'Is this the realtor you used to tour this home?',
        label: buyerSideAgent && (
          <SrBox margin="8px 0">
            <SrText weight="700" size="21" lineHeight="24px">
              Realtor:
            </SrText>
            <SrBox height="10px" />
            <SrText weight="500" size="18" lineHeight="21px" padding="8px 0 0">
              {buyerSideAgent.name} <SrBox height="8px" />
              {buyerSideAgent.email} <SrBox height="8px" />
              {buyerSideAgent.phone}
            </SrText>
          </SrBox>
        ),
        validations: ['required'],
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          choices: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
        },
      },
      supraAgents?.length > 0 && {
        id: 'supraAgent',
        title: 'Did any of these agents help you tour?',
        validations: ['required'],
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          checkboxClassName: 'agentInfo',
          choices: supraAgents.map((agent, i) => ({
            label: agent.name,
            sublabel: (agent.email || '').toLowerCase(),
            value: i,
          })),
        },
      },
    ]);
  };
}

@withTransitionHook
@withScrollToTop
@branch(({ match }) => ({
  buyersAgentResults: ['household', 'buyersAgentResultsByListingId', match.params.listingId],
}))
@withApiState({
  updateBuyersAgentApiState: {
    action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_BUYERS_AGENT,
  },
})
@withScreenId(SCREEN_ID_HOUSEHOLD_BUYER_SIDE_AGENT)
class HouseholdBuyerSideAgentPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Invite Roommates to Apply | Sunroom Rentals',
  });

  static fetchData(match, inject) {
    return HouseholdActions.findBuyersAgent(inject, parseInt(match.params.listingId, 10));
  }

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    buyersAgentResults: PropTypes.object.isRequired,
  };

  _onUpdateHouseholdBuyersAgent = (values) => {
    const {
      dispatch,
      match: { params },
    } = this.props;

    dispatch(HouseholdListingActions.updateBuyersAgent, {
      userId: parseInt(params.userId, 10),
      listingId: parseInt(params.listingId, 10),
      updates: {
        hasBuyersAgent: values.hasBuyersAgent,
        buyersAgentName: values.buyersAgentName,
        buyersAgentPhone: values.buyersAgentPhone,
        buyersAgentEmail: values.buyersAgentEmail,
      },
    }).then(() => {
      return dispatch(HouseholdActions.startApplication, {
        listingId: parseInt(params.listingId, 10),
      });
    });
  };

  _onSubmit = (values, { stepDefinition, goToNextStep }) => {
    const { buyersAgentResults } = this.props;

    switch (stepDefinition.id) {
      case 'buyer-agent': {
        this._onUpdateHouseholdBuyersAgent({
          hasBuyersAgent: values.hasBuyersAgent,
          buyersAgentName: values.hasBuyersAgent ? buyersAgentResults.buyerSideAgent.name : null,
          buyersAgentEmail: values.hasBuyersAgent ? buyersAgentResults.buyerSideAgent.email : null,
          buyersAgentPhone: values.hasBuyersAgent ? buyersAgentResults.buyerSideAgent.phone : null,
        });

        break;
      }
      case 'has-realtor': {
        if (values.hasRealtor) {
          goToNextStep();
        } else {
          this._onUpdateHouseholdBuyersAgent({
            hasBuyersAgent: false,
            buyersAgentName: null,
            buyersAgentEmail: null,
            buyersAgentPhone: null,
          });
        }
        break;
      }
      case 'supra-agent': {
        const supraAgent = buyersAgentResults.supraAgents[parseInt(values.supraAgent, 10)];

        this._onUpdateHouseholdBuyersAgent({
          hasBuyersAgent: true,
          buyersAgentName: supraAgent.name,
          buyersAgentEmail: supraAgent.email,
          buyersAgentPhone: supraAgent.phone,
        });
        break;
      }
      case 'buyers-agent-name': {
        goToNextStep();
        break;
      }
      case 'buyers-agent-phone': {
        goToNextStep();
        break;
      }
      case 'buyers-agent-email': {
        this._onUpdateHouseholdBuyersAgent({
          hasBuyersAgent: true,
          buyersAgentEmail: values.buyersAgentEmail,
          buyersAgentPhone: values.buyersAgentPhone,
          buyersAgentName: values.buyersAgentName,
        });
        break;
      }
      default: {
        throw new Error(`Handler not found for ${stepDefinition.id}`);
      }
    }
  };

  _onGoBack = () => {
    const {
      history,
      match: { params },
    } = this.props;

    const redirectUrl = `/application/${params.userId}/${params.listingId}/invite-guarantor`;
    history.push(redirectUrl);
  };

  render() {
    const { dispatch, buyersAgentResults, updateBuyersAgentApiState } = this.props;

    if (!buyersAgentResults) {
      return null;
    }

    const { buyerSideAgent, supraAgents } = buyersAgentResults;

    const getFormSteps = makeFormStepsGetterFunc({
      buyerSideAgent,
      supraAgents,
    });
    const getFormFields = makeFormFieldsGetterFunc({
      buyerSideAgent,
      supraAgents,
    });

    return (
      <SrMultiStepForm
        submitOnNext
        validateOnClickNext
        validateOnChange
        confirmLeave={false}
        dispatch={dispatch}
        getFormSteps={getFormSteps}
        getFormFields={getFormFields}
        isBusy={updateBuyersAgentApiState.inProgress}
        onSubmit={this._onSubmit}
        onBack={this._onGoBack}
      />
    );
  }
}

HouseholdBuyerSideAgentPage.displayName = 'HouseholdBuyerSideAgentPage';

export default HouseholdBuyerSideAgentPage;
